import { Database } from "@punchup/data/supabase.types";
import { QueryClient } from "@tanstack/react-query";
import { SupabaseClient } from "@supabase/supabase-js";
import { getUserComedianFollowsById } from "@/data/v2/client/user/follows";

/**
 * Prefetches user comedian follows for server-side rendering
 *
 * @param queryClient - React Query client instance
 * @param supabase - Supabase client instance
 * @param userId - Optional user ID to prefetch follows for. If not provided, uses authenticated user
 *
 * @example
 * ```tsx
 * import { prefetchUserComedianFollows } from "@/hooks/follows/prefetchUserComedianFollows";
 * import { createServerSupabaseClientWithCookies } from "@punchup/data/supabase/serverCached";
 *
 * export default async function Layout() {
 *   const queryClient = new QueryClient();
 *   const supabase = createServerSupabaseClientWithCookies();
 *   await prefetchUserComedianFollows(queryClient, supabase);
 *   // ...
 * }
 * ```
 */
export async function prefetchUserComedianFollows(
  queryClient: QueryClient,
  supabase: SupabaseClient<Database>,
  userId?: string
) {
  let targetUserId = userId;

  if (!targetUserId) {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.user) {
      return;
    }
    targetUserId = session.user.id;
  }

  await queryClient.prefetchQuery({
    queryKey: ["user-comedian-follows", targetUserId],
    queryFn: () => getUserComedianFollowsById(targetUserId as string), // typecasting to "string" because we have the null-ish check above
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
